import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
	ContactInfo, 
	ContactInfoCreateDto, 
	ContactInfoUpdateDto,
	ContactInfoBackendResponse,
	ContactInfoState,
	ContactInfoPaginatedResponse,
	ContactUsCreateDto,
	ContactUsUpdateDto,
	ContactUsBackendResponse,
	ContactUsState,
	ContactUsPaginatedResponse,
	SocialLinkCreateDto,
	SocialLinkUpdateDto,
	SocialLinkBackendResponse,
	SocialLinkState,
	SocialLinkPaginatedResponse
} from "../../modules/shared/model/contact-info.interfaces";
import { 
	createContactInfo as createContactInfoAction, 
	updateContactInfo as updateContactInfoAction, 
	getContactInfoById as getContactInfoByIdAction, 
	getContactInfos as getContactInfosAction, 
	getContactInfoByCountry as getContactInfoByCountryAction,
	deleteContactInfo as deleteContactInfoAction,
	createContactUs as createContactUsAction,
	updateContactUs as updateContactUsAction,
	getContactUsById as getContactUsByIdAction,
	getContactUsMessages as getContactUsMessagesAction,
	deleteContactUs as deleteContactUsAction,
	createSocialLink as createSocialLinkAction,
	updateSocialLink as updateSocialLinkAction,
	getSocialLinkById as getSocialLinkByIdAction,
	getSocialLinks as getSocialLinksAction,
	getLatestSocialLink as getLatestSocialLinkAction,
	getSocialLinkByType as getSocialLinkByTypeAction,
	deleteSocialLink as deleteSocialLinkAction
} from "../Actions/ContactInfoActions";

// Main ContactInfo Async Thunks
export const createContactInfo = createAsyncThunk("contactInfos/create", async (data: ContactInfoCreateDto, { rejectWithValue }) => {
	try { 
		return await createContactInfoAction(data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to create contact info"); 
	}
});

export const updateContactInfo = createAsyncThunk("contactInfos/update", async ({ id, data }: { id: string; data: ContactInfoUpdateDto }, { rejectWithValue }) => {
	try { 
		return await updateContactInfoAction(id, data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to update contact info"); 
	}
});

export const fetchContactInfo = createAsyncThunk("contactInfos/getById", async (id: string, { rejectWithValue }) => {
	try { 
		return await getContactInfoByIdAction(id); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch contact info"); 
	}
});

export const fetchContactInfos = createAsyncThunk("contactInfos/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { 
		return await getContactInfosAction(query); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch contact infos"); 
	}
});

export const fetchContactInfoByCountry = createAsyncThunk("contactInfos/getByCountry", async (countryId: string, { rejectWithValue }) => {
	try { 
		return await getContactInfoByCountryAction(countryId); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch contact info by country"); 
	}
});

export const deleteContactInfo = createAsyncThunk("contactInfos/delete", async (id: string, { rejectWithValue }) => {
	try { 
		await deleteContactInfoAction(id); 
		return id; 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to delete contact info"); 
	}
});

// Contact Us Async Thunks
export const createContactUs = createAsyncThunk("contactUs/create", async (data: ContactUsCreateDto, { rejectWithValue }) => {
	try { 
		return await createContactUsAction(data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to create contact us message"); 
	}
});

export const updateContactUs = createAsyncThunk("contactUs/update", async ({ id, data }: { id: string; data: ContactUsUpdateDto }, { rejectWithValue }) => {
	try { 
		return await updateContactUsAction(id, data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to update contact us message"); 
	}
});

export const fetchContactUs = createAsyncThunk("contactUs/getById", async (id: string, { rejectWithValue }) => {
	try { 
		return await getContactUsByIdAction(id); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch contact us message"); 
	}
});

export const fetchContactUsMessages = createAsyncThunk("contactUs/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { 
		return await getContactUsMessagesAction(query); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch contact us messages"); 
	}
});

export const deleteContactUs = createAsyncThunk("contactUs/delete", async (id: string, { rejectWithValue }) => {
	try { 
		await deleteContactUsAction(id); 
		return id; 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to delete contact us message"); 
	}
});

// Social Links Async Thunks
export const createSocialLink = createAsyncThunk("socialLinks/create", async (data: SocialLinkCreateDto, { rejectWithValue }) => {
	try { 
		return await createSocialLinkAction(data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to create social link"); 
	}
});

export const updateSocialLink = createAsyncThunk("socialLinks/update", async ({ id, data }: { id: string; data: SocialLinkUpdateDto }, { rejectWithValue }) => {
	try { 
		return await updateSocialLinkAction(id, data); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to update social link"); 
	}
});

export const fetchSocialLink = createAsyncThunk("socialLinks/getById", async (id: string, { rejectWithValue }) => {
	try { 
		return await getSocialLinkByIdAction(id); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch social link"); 
	}
});

export const fetchSocialLinks = createAsyncThunk("socialLinks/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { 
		return await getSocialLinksAction(query); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch social links"); 
	}
});

export const fetchLatestSocialLink = createAsyncThunk("socialLinks/getLatest", async (_, { rejectWithValue }) => {
	try { 
		return await getLatestSocialLinkAction(); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch latest social link"); 
	}
});

export const fetchSocialLinkByType = createAsyncThunk("socialLinks/getByType", async (typeId: number, { rejectWithValue }) => {
	try { 
		return await getSocialLinkByTypeAction(typeId); 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to fetch social link by type"); 
	}
});

export const deleteSocialLink = createAsyncThunk("socialLinks/delete", async (id: string, { rejectWithValue }) => {
	try { 
		await deleteSocialLinkAction(id); 
		return id; 
	} catch (e: any) { 
		return rejectWithValue(e?.message || "Failed to delete social link"); 
	}
});

const initialState: ContactInfoState & { contactUs: ContactUsState; socialLinks: SocialLinkState } = {
	items: [],
	current: null,
	loading: false,
	error: null,
	totalCount: 0,
	hasNextPage: false,
	hasPreviousPage: false,
	page: 1,
	limit: 10,
	contactUs: {
		items: [],
		current: null,
		loading: false,
		error: null,
		totalCount: 0,
		hasNextPage: false,
		hasPreviousPage: false,
		page: 1,
		limit: 10,
	},
	socialLinks: {
		items: [],
		current: null,
		loading: false,
		error: null,
		totalCount: 0,
		hasNextPage: false,
		hasPreviousPage: false,
		page: 1,
		limit: 10,
	}
};

const contactInfoSlice = createSlice({
	name: "contactInfos",
	initialState,
	reducers: {
		clearContactInfoError(state) {
			state.error = null;
		},
		clearCurrentContactInfo(state) {
			state.current = null;
		},
		clearContactUsError(state) {
			state.contactUs.error = null;
		},
		clearCurrentContactUs(state) {
			state.contactUs.current = null;
		},
		clearSocialLinkError(state) {
			state.socialLinks.error = null;
		},
		clearCurrentSocialLink(state) {
			state.socialLinks.current = null;
		},
	},
	extraReducers: (builder) => {
		// Main ContactInfo Reducers
		builder
			.addCase(createContactInfo.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(createContactInfo.fulfilled, (s, a: PayloadAction<ContactInfoBackendResponse>) => { 
				s.loading = false; 
				s.items.unshift(a.payload); 
				s.totalCount = (s.totalCount || 0) + 1; 
			})
			.addCase(createContactInfo.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		builder
			.addCase(updateContactInfo.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(updateContactInfo.fulfilled, (s, a: PayloadAction<ContactInfoBackendResponse>) => {
				s.loading = false;
				const i = s.items.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.items[i] = a.payload;
				if (s.current?.id === a.payload.id) s.current = a.payload;
			})
			.addCase(updateContactInfo.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		builder
			.addCase(fetchContactInfo.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(fetchContactInfo.fulfilled, (s, a: PayloadAction<ContactInfoBackendResponse>) => { 
				s.loading = false; 
				s.current = a.payload; 
			})
			.addCase(fetchContactInfo.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		builder
			.addCase(fetchContactInfos.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(fetchContactInfos.fulfilled, (s, a: PayloadAction<ContactInfoPaginatedResponse>) => { 
				s.loading = false; 
				s.items = a.payload.data || []; 
				s.totalCount = a.payload.totalCount || 0; 
				s.hasNextPage = a.payload.hasNextPage;
				s.hasPreviousPage = a.payload.hasPreviousPage;
				s.page = a.payload.page;
				s.limit = a.payload.limit;
			})
			.addCase(fetchContactInfos.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		builder
			.addCase(fetchContactInfoByCountry.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(fetchContactInfoByCountry.fulfilled, (s, a: PayloadAction<ContactInfoBackendResponse>) => { 
				s.loading = false; 
				s.current = a.payload; 
			})
			.addCase(fetchContactInfoByCountry.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		builder
			.addCase(deleteContactInfo.pending, (s) => { 
				s.loading = true; 
				s.error = null; 
			})
			.addCase(deleteContactInfo.fulfilled, (s, a: PayloadAction<string>) => { 
				s.loading = false; 
				s.items = s.items.filter(x => x.id !== a.payload); 
				if (s.current?.id === a.payload) s.current = null; 
				s.totalCount = Math.max(0, (s.totalCount || 0) - 1); 
			})
			.addCase(deleteContactInfo.rejected, (s, a: any) => { 
				s.loading = false; 
				s.error = a.payload as string; 
			});

		// Contact Us Reducers
		builder
			.addCase(createContactUs.pending, (s) => { 
				s.contactUs.loading = true; 
				s.contactUs.error = null; 
			})
			.addCase(createContactUs.fulfilled, (s, a: PayloadAction<ContactUsBackendResponse>) => { 
				s.contactUs.loading = false; 
				s.contactUs.items.unshift(a.payload); 
				s.contactUs.totalCount = (s.contactUs.totalCount || 0) + 1; 
			})
			.addCase(createContactUs.rejected, (s, a: any) => { 
				s.contactUs.loading = false; 
				s.contactUs.error = a.payload as string; 
			});

		builder
			.addCase(updateContactUs.pending, (s) => { 
				s.contactUs.loading = true; 
				s.contactUs.error = null; 
			})
			.addCase(updateContactUs.fulfilled, (s, a: PayloadAction<ContactUsBackendResponse>) => {
				s.contactUs.loading = false;
				const i = s.contactUs.items.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.contactUs.items[i] = a.payload;
				if (s.contactUs.current?.id === a.payload.id) s.contactUs.current = a.payload;
			})
			.addCase(updateContactUs.rejected, (s, a: any) => { 
				s.contactUs.loading = false; 
				s.contactUs.error = a.payload as string; 
			});

		builder
			.addCase(fetchContactUs.pending, (s) => { 
				s.contactUs.loading = true; 
				s.contactUs.error = null; 
			})
			.addCase(fetchContactUs.fulfilled, (s, a: PayloadAction<ContactUsBackendResponse>) => { 
				s.contactUs.loading = false; 
				s.contactUs.current = a.payload; 
			})
			.addCase(fetchContactUs.rejected, (s, a: any) => { 
				s.contactUs.loading = false; 
				s.contactUs.error = a.payload as string; 
			});

		builder
			.addCase(fetchContactUsMessages.pending, (s) => { 
				s.contactUs.loading = true; 
				s.contactUs.error = null; 
			})
			.addCase(fetchContactUsMessages.fulfilled, (s, a: PayloadAction<ContactUsPaginatedResponse>) => { 
				s.contactUs.loading = false; 
				s.contactUs.items = a.payload.data || []; 
				s.contactUs.totalCount = a.payload.totalCount || 0; 
				s.contactUs.hasNextPage = a.payload.hasNextPage;
				s.contactUs.hasPreviousPage = a.payload.hasPreviousPage;
				s.contactUs.page = a.payload.page;
				s.contactUs.limit = a.payload.limit;
			})
			.addCase(fetchContactUsMessages.rejected, (s, a: any) => { 
				s.contactUs.loading = false; 
				s.contactUs.error = a.payload as string; 
			});

		builder
			.addCase(deleteContactUs.pending, (s) => { 
				s.contactUs.loading = true; 
				s.contactUs.error = null; 
			})
			.addCase(deleteContactUs.fulfilled, (s, a: PayloadAction<string>) => { 
				s.contactUs.loading = false; 
				s.contactUs.items = s.contactUs.items.filter(x => String(x.id) !== a.payload); 
				if (String(s.contactUs.current?.id) === a.payload) s.contactUs.current = null; 
				s.contactUs.totalCount = Math.max(0, (s.contactUs.totalCount || 0) - 1); 
			})
			.addCase(deleteContactUs.rejected, (s, a: any) => { 
				s.contactUs.loading = false; 
				s.contactUs.error = a.payload as string; 
			});

		// Social Links Reducers
		builder
			.addCase(createSocialLink.pending, (s) => { 
				s.socialLinks.loading = true; 
				s.socialLinks.error = null; 
			})
			.addCase(createSocialLink.fulfilled, (s, a: PayloadAction<SocialLinkBackendResponse>) => { 
				s.socialLinks.loading = false; 
				s.socialLinks.items.unshift(a.payload); 
				s.socialLinks.totalCount = (s.socialLinks.totalCount || 0) + 1; 
			})
			.addCase(createSocialLink.rejected, (s, a: any) => { 
				s.socialLinks.loading = false; 
				s.socialLinks.error = a.payload as string; 
			});

		builder
			.addCase(updateSocialLink.pending, (s) => { 
				s.socialLinks.loading = true; 
				s.socialLinks.error = null; 
			})
			.addCase(updateSocialLink.fulfilled, (s, a: PayloadAction<SocialLinkBackendResponse>) => {
				s.socialLinks.loading = false;
				const i = s.socialLinks.items.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.socialLinks.items[i] = a.payload;
				if (s.socialLinks.current?.id === a.payload.id) s.socialLinks.current = a.payload;
			})
			.addCase(updateSocialLink.rejected, (s, a: any) => { 
				s.socialLinks.loading = false; 
				s.socialLinks.error = a.payload as string; 
			});

		builder
			.addCase(fetchSocialLink.pending, (s) => { 
				s.socialLinks.loading = true; 
				s.socialLinks.error = null; 
			})
			.addCase(fetchSocialLink.fulfilled, (s, a: PayloadAction<SocialLinkBackendResponse>) => { 
				s.socialLinks.loading = false; 
				s.socialLinks.current = a.payload; 
			})
			.addCase(fetchSocialLink.rejected, (s, a: any) => { 
				s.socialLinks.loading = false; 
				s.socialLinks.error = a.payload as string; 
			});

		builder
			.addCase(fetchSocialLinks.pending, (s) => { 
				s.socialLinks.loading = true; 
				s.socialLinks.error = null; 
			})
			.addCase(fetchSocialLinks.fulfilled, (s, a: PayloadAction<SocialLinkPaginatedResponse>) => { 
				s.socialLinks.loading = false; 
				s.socialLinks.items = a.payload.data || []; 
				s.socialLinks.totalCount = a.payload.totalCount || 0; 
				s.socialLinks.hasNextPage = a.payload.hasNextPage;
				s.socialLinks.hasPreviousPage = a.payload.hasPreviousPage;
				s.socialLinks.page = a.payload.page;
				s.socialLinks.limit = a.payload.limit;
			})
			.addCase(fetchSocialLinks.rejected, (s, a: any) => { 
				s.socialLinks.loading = false; 
				s.socialLinks.error = a.payload as string; 
			});

		builder
			.addCase(fetchLatestSocialLink.pending, (s) => { 
				s.socialLinks.loading = true; 
				s.socialLinks.error = null; 
			})
			.addCase(fetchLatestSocialLink.fulfilled, (s, a: PayloadAction<SocialLinkBackendResponse>) => { 
				s.socialLinks.loading = false; 
				s.socialLinks.current = a.payload; 
			})
			.addCase(fetchLatestSocialLink.rejected, (s, a: any) => { 
				s.socialLinks.loading = false; 
				s.socialLinks.error = a.payload as string; 
			});

		builder
			.addCase(fetchSocialLinkByType.pending, (s) => { 
				s.socialLinks.loading = true; 
				s.socialLinks.error = null; 
			})
			.addCase(fetchSocialLinkByType.fulfilled, (s, a: PayloadAction<SocialLinkBackendResponse>) => { 
				s.socialLinks.loading = false; 
				s.socialLinks.current = a.payload; 
			})
			.addCase(fetchSocialLinkByType.rejected, (s, a: any) => { 
				s.socialLinks.loading = false; 
				s.socialLinks.error = a.payload as string; 
			});

		builder
			.addCase(deleteSocialLink.pending, (s) => { 
				s.socialLinks.loading = true; 
				s.socialLinks.error = null; 
			})
			.addCase(deleteSocialLink.fulfilled, (s, a: PayloadAction<string>) => { 
				s.socialLinks.loading = false; 
				s.socialLinks.items = s.socialLinks.items.filter(x => x.id !== a.payload); 
				if (s.socialLinks.current?.id === a.payload) s.socialLinks.current = null; 
				s.socialLinks.totalCount = Math.max(0, (s.socialLinks.totalCount || 0) - 1); 
			})
			.addCase(deleteSocialLink.rejected, (s, a: any) => { 
				s.socialLinks.loading = false; 
				s.socialLinks.error = a.payload as string; 
			});
	}
});

export const { clearContactInfoError, clearCurrentContactInfo, clearContactUsError, clearCurrentContactUs, clearSocialLinkError, clearCurrentSocialLink } = contactInfoSlice.actions;
export default contactInfoSlice.reducer;
